// Area Intelligence — profile a drawn Maps polygon with U.S. Census / ACS data.
//
// First pass: resolves the census tract at the polygon's centroid (via the
// FCC Census Block API, which is CORS-friendly), then pulls ACS 5-year
// estimates for that tract from the public Census API (no key required for
// low volume). A polygon can span several tracts; v1 reports the centroid's
// tract and notes that. Larger areas → multi-tract aggregation is a future
// enhancement.

export interface AreaProfile {
  tractName: string;        // e.g. "Census Tract 1042.01, Maricopa County, Arizona"
  population: number | null;
  medianAge: number | null;
  medianHouseholdIncome: number | null;
  households: number | null;
  medianHomeValue: number | null;
  medianGrossRent: number | null;
  acsYear: string;
  note: string;
}

interface LatLng { lat: number; lng: number }

const centroidOf = (paths: LatLng[]): LatLng => {
  const n = paths.length || 1;
  const sum = paths.reduce((a, p) => ({ lat: a.lat + p.lat, lng: a.lng + p.lng }), { lat: 0, lng: 0 });
  return { lat: sum.lat / n, lng: sum.lng / n };
};

const num = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  // Census uses large negative sentinels (e.g. -666666666) for "no data".
  if (!isFinite(n) || n <= -100000) return null;
  return n;
};

const ACS_YEAR = '2022';
const ACS_VARS: Record<string, string> = {
  B01003_001E: 'population',
  B01002_001E: 'medianAge',
  B19013_001E: 'medianHouseholdIncome',
  B11001_001E: 'households',
  B25077_001E: 'medianHomeValue',
  B25064_001E: 'medianGrossRent',
};

export const areaIntelligenceService = {
  async getAreaProfile(paths: LatLng[]): Promise<AreaProfile> {
    if (!paths || paths.length < 3) {
      throw new Error('Draw an area with at least 3 points first.');
    }
    const { lat, lng } = centroidOf(paths);

    // 1. Resolve the census tract FIPS at the centroid via the FCC API.
    const fccUrl = `https://geo.fcc.gov/api/census/block/find?latitude=${lat}&longitude=${lng}&censusYear=2020&format=json`;
    const fccRes = await fetch(fccUrl);
    if (!fccRes.ok) throw new Error('Could not resolve the census area for this location.');
    const fcc = await fccRes.json();
    const fips: string = fcc?.Block?.FIPS || '';
    if (!fips || fips.length < 11) {
      throw new Error('No U.S. census tract found for this area (is it inside the United States?).');
    }
    const state = fips.slice(0, 2);
    const county = fips.slice(2, 5);
    const tract = fips.slice(5, 11);

    // 2. Pull ACS 5-year estimates for that tract.
    const vars = Object.keys(ACS_VARS).join(',');
    const acsUrl = `https://api.census.gov/data/${ACS_YEAR}/acs/acs5?get=NAME,${vars}&for=tract:${tract}&in=state:${state}%20county:${county}`;
    const acsRes = await fetch(acsUrl);
    if (!acsRes.ok) throw new Error('Census data is temporarily unavailable. Please try again.');
    const rows: string[][] = await acsRes.json();
    if (!Array.isArray(rows) || rows.length < 2) {
      throw new Error('No census data returned for this area.');
    }
    const header = rows[0];
    const values = rows[1];
    const get = (code: string) => values[header.indexOf(code)];

    return {
      tractName: get('NAME') || `Tract ${tract}`,
      population: num(get('B01003_001E')),
      medianAge: num(get('B01002_001E')),
      medianHouseholdIncome: num(get('B19013_001E')),
      households: num(get('B11001_001E')),
      medianHomeValue: num(get('B25077_001E')),
      medianGrossRent: num(get('B25064_001E')),
      acsYear: ACS_YEAR,
      note: 'Based on the U.S. Census tract at the center of the drawn area (ACS 5-year estimates). Larger areas may span multiple tracts.',
    };
  },
};
